import type { ColumnDef } from '@tanstack/react-table'
import type { ITenant } from '@/shared/api/types/tenant-types'

import type { PaginationConfigInput } from '@/shared/ui/data-table'
import { format, parseISO } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TenantStatusToggle } from '@/features/tenant/tenant-status-toggle'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { DataTable } from '@/shared/ui/data-table'

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm')
  } catch {
    return '—'
  }
}

interface TenantListTableProps {
  tenants: ITenant[]
  loading: boolean
  pagination: PaginationConfigInput
  onPageChange: (page: number, pageSize: number) => void
  onView: (tenant: ITenant) => void
}
export function TenantListTable({
  tenants,
  loading,
  pagination,
  onPageChange,
  onView,
}: TenantListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<ITenant>[]>(
    () => [
      {
        id: 'name',
        header: t('tenant.name'),
        cell: ({ row }) => (
          <div className="flex min-w-[240px] items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={row.original.logoUrl} alt={row.original.name} />
              <AvatarFallback>{row.original.name.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-zinc-900">{row.original.name}</p>
              <p className="truncate text-xs text-zinc-500">{row.original.slug}</p>
            </div>
          </div>
        ),
      },
      {
        id: 'website',
        header: t('tenant.website'),
        cell: ({ row }) =>
          row.original.website ? (
            <a
              href={row.original.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.website}
            </a>
          ) : (
            '—'
          ),
      },
      {
        accessorKey: 'contactEmail',
        header: t('tenant.contactEmail'),
      },
      {
        id: 'updatedAt',
        header: t('common.updatedAt', { defaultValue: 'Updated at' }),
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
      {
        id: 'isActive',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <TenantStatusToggle tenantId={row.original.id} isActive={row.original.isActive} />
        ),
      },
    ],
    [t],
  )

  return (
    <DataTable
      columns={columns}
      data={tenants}
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: onPageChange,
      }}
      onRowClick={onView}
    />
  )
}
